import { isTreeField, isTreeSection, isTreeTable, isTreeOverlay } from "@/helpers/Misc";
import LookupService from "./LookupService";

export default class Structure {
  public structureJSON: IStructure = {} as IStructure;
  public ldts: Map<string, ILdt> = new Map();
  public fields: Map<string, ITreeField> = new Map();
  public sections: Map<string, ITreeSection> = new Map();
  public overlays: Map<string, any> = new Map();
  public functionality: { [key: string]: any } = {};
  public xpathMap: string[] = new Array<string>();
  public formName: string = "";
  public tree: ITreeField = {} as ITreeField;
  public lookups = LookupService;

  //Todo: rozsekat funkcionalitu do preprocessing funkcii - nech su upravovatelne / extendovatelne a nie na pevno setnute;
  public structurePreprocessingActions: Array<Function> = [];

  public async loadStructureJSON(initialStructureJSON: IStructure) {
    window.FormEngine.events.publish(window.FormEngine.events.list.STRUCTURE_BEFORE_LOAD, initialStructureJSON);
    this.structureJSON = initialStructureJSON;
    await this.initStructure();
    window.FormEngine.events.publish(window.FormEngine.events.list.STRUCTURE_AFTER_LOAD, this);
  }

  public async initStructure() {
    if (this.structureJSON.form_name) {
      this.formName = this.structureJSON.form_name;
    }
    if (this.structureJSON.ldts) {
      for (let ldtName in this.structureJSON.ldts) {
        let tempLdt = this.structureJSON.ldts[ldtName];
        this.ldts.set(ldtName, tempLdt);
      }
    }
    if (this.structureJSON.lookups) {
      let tempLookups = this.structureJSON.lookups as { [key: string]: { [key: string]: ILookup } | [ILookup] };
      await this.lookups.loadLookups(tempLookups);
      this.lookups.loadExternalLookups();
    }
    if (this.structureJSON.functionality) {
      this.functionality = this.structureJSON.functionality;

    }
    let treeStructure: any = {};
    if (this.structureJSON.tree) {
      treeStructure = this.initMetadataFromTree(this.structureJSON.tree);
    }

    this.tree = treeStructure;
  }

  /*
    inicializacia metadat pre kontrolky vo window.structure a nasledna inicializacia this.fields
    -rekurzivne prehlada structureJSON.tree a v pripade konecnej vetvy (control mtdt)
     priradi dalsie konfiguracie (LDT, loolups, ...) nasledne ulozi referenciu do this.fields
    -v pripade tabulky ulozi najdene identifikatory kontroliek do metadat pre tabulku
    **ide o priame referencie na window.structure objekt
  */
  public initMetadataFromTree(branch: any) {
    let finalBranch: any = {};
    for (let controlName in branch) {
      let childBranch = branch[controlName];
      childBranch.Identifier = controlName;

      switch (true) {
        case isTreeTable(childBranch): {
          this.mapTable(childBranch);
          finalBranch[controlName] = this.fields.get(controlName);
          break;
        }
        case isTreeField(childBranch): {
          this.setField(controlName, childBranch);
          finalBranch[controlName] = this.fields.get(controlName);
          if (finalBranch[controlName].XpathEdoc) {
            this.xpathMap.push(finalBranch[controlName].XpathEdoc);
          }
          if (childBranch.Tree) {
            finalBranch[controlName].Tree = this.initMetadataFromTree(childBranch.Tree);
          }
          break;
        }
        case isTreeSection(childBranch): {
          finalBranch[controlName] = childBranch as ITreeSection;
          finalBranch[controlName].Tree = this.initMetadataFromTree(finalBranch[controlName].Tree);
          this.sections.set(controlName, finalBranch[controlName]);
          break;
        }
        case isTreeOverlay(childBranch): {
          finalBranch[controlName] = childBranch as ITreeSection;
          finalBranch[controlName].Tree = this.initMetadataFromTree(finalBranch[controlName].Tree);
          this.overlays.set(controlName, finalBranch[controlName]);
          break;
        }
        default: {
          console.log("Structure processing does not recognize this branch type:", childBranch);
          break;
        }
      }
    }

    return finalBranch;
  }

  private mapTable(field: ITreeTable) {
    for (let controlName in field.Tree) {
      if (!field.Tree[controlName].Tree) {
        field.Tree[controlName].IsTableDetailElement = true;
        this.setField(controlName, field.Tree[controlName] as ITreeField);
      }
    }

    if (field.Detail && field.Detail.Tree) {
      //console.log(field.Detail);
      field.Detail.Tree = this.initMetadataFromTree(field.Detail.Tree);
      this.sections.set(field.Detail.Identifier, field.Detail);
    }

    return this.setField(field.Identifier, field as ITreeField);
  }

  private setField(fieldName: string, field: ITreeField) {
    if (field.FieldLogicalDataTypeID) {
      field.LDT = this.ldts.get(String(field.FieldLogicalDataTypeID)) as ILdt;
    }
    this.fields.set(fieldName, field);
  }

  /**
   *  ====================== UTILITY FILTER FUNCTIONS ======================
   */

  public filterSections(filterPredicate: any, branch?: ITree): Array<ITree> {
    let results = [];
    if (branch) {
      results = this.getAllSectionsArray(branch);
    } else {
      results = this.getAllSectionsArray();
    }
    return results.filter(filterPredicate);
  }

  private _getAllSections(branch: ITree, results: Array<ITree | ITreeField | ITreeSection>) {
    for (let propName in branch) {
      let prop = branch[propName];
      prop.Identifier = propName;
      if (prop.Tree) {
        if ((prop as ITreeSection).MaxRepetition) {
          results.push(prop as ITreeField);
        }
        this._getAllSections(prop.Tree, results);
      }
    }
  }

  public getAllSectionsArray(branch?: ITree) {
    let results = new Array<ITree>();
    this._getAllSections(branch ? branch : (this.structureJSON.tree as ITree), results);

    return results;
  }
  public filterControls(filterPredicate: any, branch?: ITree): Array<ITreeField> {
    let results = [];
    if (branch) {
      results = this.getAllControlsArray(branch);
    } else {
      results = this.getAllControlsArray();
    }
    return results.filter(filterPredicate);
  }

  private _getAllControls(branch: ITree, results: Array<ITree | ITreeField>) {
    for (let propName in branch) {
      let prop = branch[propName];
      prop.Identifier = propName;
      if (!prop.Tree) {
        results.push(prop as ITreeField);
      } else {
        this._getAllControls(prop.Tree, results);
      }
    }
  }

  public getAllControlsArray(branch?: ITree) {
    let results = new Array<ITreeField>();
    this._getAllControls(branch ? branch : (this.structureJSON.tree as ITree), results);

    return results;
  }
}
